/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { Tooltip } from 'radix-ui';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import { useAccount } from '~/apis';
import {
  FullscreenContext,
  selectConnectionTimeout,
  selectGameId,
  selectGameType,
  selectHorseAmount,
  selectHorses,
  selectIsGameRunning,
  selectPlayers,
  useSelector,
  useTranslation,
} from '~/app';
import { config } from '~/app/config';
import { useAuthState } from '~/auth';
import { ReactComponent as LogoutIcon } from '~/components/AccountMenu/logout.svg';
import ClientOnly from '~/components/ClientOnly';
import ConnectionTimeoutDialog from '~/components/ConnectionTimeoutDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/DropdownMenu';
import { LanguageSubmenu } from '~/components/LanguageSubmenu';
import { GameType } from '~/server/src/dtos';
import { cn } from '~/utils/styles';

import { ReactComponent as RefreshIcon } from '../bar-bottom/refresh.svg';
import azaspieleLogo from '../button-icons/azaspiele.png';
import { ReactComponent as ImgExpand } from '../button-icons/button-expand.svg';
import { ReactComponent as ImgInstructions } from '../button-icons/button-instructions.svg';
import { ReactComponent as ImgInvitation } from '../button-icons/button-invitation.svg';
import { ReactComponent as ImgRankings } from '../button-icons/button-rankings.svg';
import { ReactComponent as ImgReport } from '../button-icons/button-report.svg';
import { ReactComponent as ImgRetract } from '../button-icons/button-retract.svg';
import { ReactComponent as ImgStatistics } from '../button-icons/button-statistics.svg';
import { ReactComponent as ImgTable } from '../button-icons/button-table.svg';
import { ReactComponent as ImgYouTube } from '../button-icons/button-youtube.svg';
import { ReactComponent as ImgDiscord } from '../button-icons/discord.svg';
import { ReactComponent as ImgWhatsApp } from '../button-icons/whatsapp.svg';
import InvitationDialog from '../InvitationDialog';
import HamburgerMenuIcon from './HamburgerMenuIcon';

export type FloatingMenuItemKey =
  | 'table'
  | 'worldRankings'
  | 'statistics'
  | 'report'
  | 'link'
  | 'instructions'
  | 'youtube'
  | 'discord'
  | 'whatsapp'
  | 'developer'
  | 'leaveGame'
  | 'history';

interface Props {
  onItemClicked: (key: FloatingMenuItemKey, event?: Event) => void;
  className?: string;
}

interface FloatingAction {
  key: FloatingMenuItemKey | 'fullscreen' | 'refresh';
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  showAsFAB?: boolean;
  showInMenu?: boolean;
  condition?: boolean;
  /** Priority for responsive display: higher = shown earlier. Important buttons: statistics=10, history=9, invite=8, refresh=5, fullscreen=4 */
  priority?: number;
}

const FloatingMenu = ({ onItemClicked, className }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { isFullscreen, toggleIsFullscreen } = useContext(FullscreenContext);
  const account = useAccount();
  const { isAuthorized } = useAuthState();
  const gameId = useSelector(selectGameId);
  const gameType = useSelector(selectGameType);
  const connectionTimeout = useSelector(selectConnectionTimeout);
  const isGameRunning = useSelector(selectIsGameRunning);
  const horses = useSelector(selectHorses);
  const players = useSelector(selectPlayers);
  const horseAmount = useSelector(selectHorseAmount);
  const [isConnectionTimeoutEnabled, setIsConnectionTimeoutEnabled] = useLocalStorageState(
    'isConnectionTimeoutEnabled',
    { defaultValue: true },
  );
  const showConnectionTimeoutHint = isConnectionTimeoutEnabled && connectionTimeout;
  const [isOpen, setIsOpen] = useState(false);
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);

  // Show leave FAB in top bar when game is NOT running (e.g. waiting for players)
  const shouldShowLeaveInTopBarFAB = !isGameRunning;

  // Refs for buttons
  const actualRefreshButtonRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const actualMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Calculate header space usage: players * (horses per player + badge width)
  const playerCount = useMemo(() => {
    if (!horses || horses.length === 0) return players?.length ?? 0;
    const firstPlayerHorses = horses.filter((h) => h.playerId === horses[0].playerId).length;
    if (firstPlayerHorses === 0) return players?.length ?? 0;
    return horseAmount / firstPlayerHorses;
  }, [horses, horseAmount, players]);

  const horsesPerPlayer = useMemo(() => {
    if (!horses || horses.length === 0) return 0;
    const firstPlayerHorses = horses.filter((h) => h.playerId === horses[0].playerId).length;
    return firstPlayerHorses || 0;
  }, [horses]);

  // Calculate available space for FABs based on screen width and header content
  // Header takes: ~56px (menu button) + players * (~120px base + ~40px per horse) + ~200px (right section) + padding
  // On very small screens (< 640px), all FABs move to overflow menu
  const [availableFabSlots, setAvailableFabSlots] = useState(0);
  useEffect(() => {
    const calculateSlots = () => {
      const width = window.innerWidth;
      // On very small screens (below sm breakpoint ~640px), show no FABs - all go to menu
      if (width < 640) {
        setAvailableFabSlots(0);
        return;
      }
      // More accurate header width estimation:
      // - Menu button: 40px (w-10)
      // - Per player: ~120px base (badge) + ~40px per horse + gap (~20px between players)
      // - Right section (dice/cards): ~200px
      // - Padding/margins: ~100px total
      const playerColumnWidth =
        playerCount > 0 ? playerCount * (120 + horsesPerPlayer * 40 + 20) : 0;
      const headerWidth = 40 + playerColumnWidth + 200 + 100;
      const availableWidth = width - headerWidth;
      // Each FAB needs w-10 (2.5rem ≈ 40px) + 8px gap = ~48px, minimum 0 slots (all in menu), max 6 slots
      // Add some buffer (20%) to account for spacing and ensure buttons don't overlap
      const slots = Math.max(0, Math.min(6, Math.floor((availableWidth * 0.8) / 48)));
      setAvailableFabSlots(slots);
    };
    calculateSlots();
    const resizeObserver = new ResizeObserver(calculateSlots);
    resizeObserver.observe(document.body);
    window.addEventListener('resize', calculateSlots);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateSlots);
    };
  }, [playerCount, horsesPerPlayer]);

  const handleDropdownOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  // Define all actions in a single source of truth
  const actions: FloatingAction[] = useMemo(
    () => [
      {
        key: 'statistics',
        label: t('game_menuStatistics'),
        icon: <ImgStatistics className="block h-4 w-4 md:h-5 md:w-5" />,
        onClick: () => onItemClicked('statistics'),
        showAsFAB: true,
        showInMenu: true,
        priority: 10, // Highest priority
      },
      {
        key: 'link',
        label: isGameRunning ? t('game_menuLink_spectate') : t('game_menuLink'),
        icon: <ImgInvitation className="block h-4 w-4 md:h-5 md:w-5" />,
        onClick: () => setIsInvitationDialogOpen(true),
        showAsFAB: true,
        showInMenu: true,
        condition: gameId !== undefined && gameType !== GameType.Training,
        priority: 8, // High priority
      },
      {
        key: 'refresh',
        label: t('refresh'),
        icon: <RefreshIcon className="block h-4 w-4 md:h-5 md:w-5" />,
        onClick: () => window.location.reload(),
        showAsFAB: true,
        showInMenu: true,
        priority: 5, // Medium priority
      },
      {
        key: 'fullscreen',
        label: isFullscreen ? t('game_menuFullscreenExit') : t('game_menuFullscreen'),
        icon: isFullscreen ? (
          <ImgRetract className="block h-4 w-4 md:h-5 md:w-5" />
        ) : (
          <ImgExpand className="block h-4 w-4 md:h-5 md:w-5" />
        ),
        onClick: toggleIsFullscreen,
        showAsFAB: true,
        showInMenu: true,
        priority: 4, // Lower priority
      },
      {
        key: 'table',
        label: t('game_menuTable'),
        icon: <ImgTable className="h-6 w-6" />,
        onClick: () => onItemClicked('table'),
        showAsFAB: false,
        showInMenu: true,
      },
      {
        key: 'worldRankings',
        label: t('game_menuWorldRankings'),
        icon: <ImgRankings className="h-6 w-6" />,
        onClick: () => onItemClicked('worldRankings'),
        showAsFAB: false,
        showInMenu: true,
      },
      {
        key: 'instructions',
        label: t('button_instructions'),
        icon: <ImgInstructions className="h-6 w-6" />,
        onClick: () => onItemClicked('instructions'),
        showAsFAB: false,
        showInMenu: true,
      },
      {
        key: 'youtube',
        label: t('button_youtube'),
        icon: <ImgYouTube className="h-6 w-6" />,
        onClick: () => onItemClicked('youtube'),
        showAsFAB: false,
        showInMenu: true,
      },
      {
        key: 'discord',
        label: t('button_discord'),
        icon: <ImgDiscord className="h-6 w-6" />,
        onClick: () => onItemClicked('discord'),
        showAsFAB: false,
        showInMenu: true,
        condition: config.discordEnabled && !!config.discordInviteUrl,
      },
      {
        key: 'whatsapp',
        label: t('button_whatsapp'),
        icon: <ImgWhatsApp className="h-6 w-6" />,
        onClick: () => onItemClicked('whatsapp'),
        showAsFAB: false,
        showInMenu: true,
        condition: config.whatsappEnabled && !!config.whatsappInviteUrl,
      },
      {
        key: 'developer',
        label: t('button_developer'),
        icon: (
          <img
            src={azaspieleLogo}
            alt="AZA-Spiele"
            style={{
              height: 24,
              width: 'auto',
              display: 'block',
              objectFit: 'contain',
              filter: 'drop-shadow(0 1px 2px #02666644)',
            }}
            className="block h-6 w-6 object-contain"
          />
        ),
        onClick: () => onItemClicked('developer'),
        showAsFAB: false,
        showInMenu: true,
      },
      {
        key: 'report',
        label: t('game_menuReport'),
        icon: <ImgReport className="h-6 w-6" />,
        onClick: () => onItemClicked('report'),
        showAsFAB: false,
        showInMenu: true,
        condition: true, // Always show in menu; disabled when not logged in
      },
      {
        key: 'leaveGame',
        label: t('button_leaveGame'),
        icon: <LogoutIcon className="h-4 w-4 md:h-5 md:w-5" />,
        onClick: () => onItemClicked('leaveGame'),
        showAsFAB: shouldShowLeaveInTopBarFAB,
        showInMenu: false, // Rendered separately at bottom of dropdown
        condition: true, // Always available; FAB when !isGameRunning, menu item always shown
        priority: 6, // Above refresh (5) so Leave is visible when slot count is limited
      },
    ],
    [
      t,
      isFullscreen,
      toggleIsFullscreen,
      onItemClicked,
      gameId,
      gameType,
      isGameRunning,
      shouldShowLeaveInTopBarFAB,
    ],
  );

  // Filter actions based on their conditions
  const availableActions = actions.filter((action) => action.condition !== false);
  const fabActions = availableActions.filter((action) => action.showAsFAB);

  // Sort FABs by priority (highest first), then filter to show only top N based on available slots
  const sortedFabActions = useMemo(() => {
    return [...fabActions].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }, [fabActions]);

  const visibleFabActions = useMemo(() => {
    return sortedFabActions.slice(0, availableFabSlots);
  }, [sortedFabActions, availableFabSlots]);

  // Exclude leaveGame from fabActions for menu display (it will be shown separately at the end)
  const fabActionsForMenu = fabActions.filter((action) => action.key !== 'leaveGame');

  return (
    <ClientOnly>
      <Tooltip.Provider>
        <>
          {/* Tooltip for connection timeout, attached to refresh or menu button */}
          <Tooltip.Root open={!!showConnectionTimeoutHint}>
            <Tooltip.Trigger asChild>
              <div className="pointer-events-none absolute inset-0">
                <div
                  ref={menuButtonRef}
                  className="h-9 w-9 md:h-10 md:w-10"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                sideOffset={12}
                className="relative z-9999"
                collisionPadding={16}
                side="top"
                align="center"
                avoidCollisions={true}
              >
                <ConnectionTimeoutDialog
                  open={true}
                  onClose={() => {}}
                  onHidePermanently={() => {
                    setIsConnectionTimeoutEnabled(false);
                  }}
                  onNotNow={() => {}}
                />
                <Tooltip.Arrow className="fill-white drop-shadow-xs" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>

          <DropdownMenu open={isOpen} onOpenChange={handleDropdownOpenChange} modal={false}>
            {/* Responsive FAB Layout */}
            <div className={cn('z-50 flex gap-x-2 gap-y-2', className)}>
              {/* Hamburger Menu Button - Always visible on all screen sizes */}
              <div className="relative flex">
                <DropdownMenuTrigger asChild>
                  <button
                    ref={actualMenuButtonRef}
                    className={cn(
                      'h-9 w-9 md:h-10 md:w-10',
                      'flex cursor-pointer items-center justify-center rounded-full bg-white text-black shadow-lg transition-all duration-200 hover:bg-primary-light focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden',
                    )}
                    aria-label={t('button_menuTooltip')}
                  >
                    <HamburgerMenuIcon className="block h-4 w-4 transition-transform md:h-5 md:w-5" />
                  </button>
                </DropdownMenuTrigger>
              </div>

              {/* Individual FABs - Responsive display based on available space */}
              {visibleFabActions.length > 0 && (
                <div className="flex gap-x-2 gap-y-2">
                  {/* Individual Action FABs - shown based on priority and available slots */}
                  {visibleFabActions.map((action, _index) => {
                    return (
                      <div key={action.key} className="shrink-0">
                        <Tooltip.Root delayDuration={0}>
                          <Tooltip.Trigger asChild>
                            <button
                              ref={action.key === 'refresh' ? actualRefreshButtonRef : undefined}
                              className={cn(
                                'h-9 w-9 md:h-10 md:w-10',
                                'flex cursor-pointer items-center justify-center rounded-full shadow-lg transition-all duration-200 focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
                                '[&_svg]:fill-current [&_svg]:stroke-current',
                                action.key === 'leaveGame'
                                  ? 'border-2 border-red-500 bg-white text-red-600 hover:bg-red-50 focus:ring-red-500'
                                  : 'bg-white text-primary hover:bg-primary-light focus:ring-primary',
                              )}
                              aria-label={action.label}
                              data-action={action.key}
                              onClick={action.onClick}
                            >
                              {action.icon}
                            </button>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              sideOffset={8}
                              className="rounded-lg border border-gray-100 bg-white px-2 py-2 text-sm shadow-xl"
                            >
                              {action.label}
                              <Tooltip.Arrow className="fill-white drop-shadow-xs" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Dropdown menu content */}
            <DropdownMenuContent
              sideOffset={12}
              align="start"
              className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-xl"
            >
              {/* Primary Actions - Shown in menu for FABs that don't fit on screen */}
              {(() => {
                const hiddenFabActions = fabActionsForMenu.filter(
                  (action) => !visibleFabActions.some((v) => v.key === action.key),
                );
                return hiddenFabActions.length > 0 ? (
                  <div>
                    {hiddenFabActions.map((action, index) => (
                      <div key={action.key}>
                        <DropdownMenuItem
                          className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                          onSelect={action.onClick}
                          data-action={action.key}
                        >
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                            {action.icon}
                          </span>
                          <span className="flex items-center">{action.label}</span>
                        </DropdownMenuItem>
                        {index === hiddenFabActions.length - 1 && <DropdownMenuSeparator />}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}

              {/* Group 1: Game Info */}
              <DropdownMenuItem
                className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                onSelect={() => onItemClicked('table')}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <ImgTable className="h-6 w-6" />
                </span>
                <span className="flex items-center">{t('game_menuTable')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                onSelect={() => onItemClicked('worldRankings')}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <ImgRankings className="h-6 w-6" />
                </span>
                <span className="flex items-center">{t('game_menuWorldRankings')}</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* Group 2: Help & Support */}
              <DropdownMenuItem
                className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                onSelect={() => onItemClicked('instructions')}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <ImgInstructions className="h-6 w-6" />
                </span>
                <span className="flex items-center">{t('button_instructions')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                onSelect={() => onItemClicked('youtube')}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <ImgYouTube className="h-6 w-6" />
                </span>
                <span className="flex items-center">{t('button_youtube')}</span>
              </DropdownMenuItem>
              {config.discordEnabled && config.discordInviteUrl && (
                <DropdownMenuItem
                  className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                  onSelect={() => onItemClicked('discord')}
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <ImgDiscord className="h-6 w-6" />
                  </span>
                  <span className="flex items-center">{t('button_discord')}</span>
                </DropdownMenuItem>
              )}
              {config.whatsappEnabled && config.whatsappInviteUrl && (
                <DropdownMenuItem
                  className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                  onSelect={() => onItemClicked('whatsapp')}
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <ImgWhatsApp className="h-6 w-6" />
                  </span>
                  <span className="flex items-center">{t('button_whatsapp')}</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                onSelect={() => onItemClicked('developer')}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <img
                    src={azaspieleLogo}
                    alt="AZA-Spiele"
                    style={{
                      height: 24,
                      width: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 1px 2px #02666644)',
                    }}
                    className="block h-6 w-6 object-contain"
                  />
                </span>
                <span className="flex items-center">{t('button_developer')}</span>
              </DropdownMenuItem>

              {/* Group 4: Settings */}
              <LanguageSubmenu />

              {/* Separator before Report (always show so Report is visible in training when logged out) */}
              <DropdownMenuSeparator />

              {/* Group 5: Utility – Report always visible; disabled with inline hint when not logged in (no tooltip: dropdown would close) */}
              <DropdownMenuItem
                data-component="FloatingMenuItemReport"
                disabled={!(account && isAuthorized)}
                className={cn(
                  'flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg',
                  account && isAuthorized
                    ? 'hover:bg-primary-light/20 focus:bg-primary-light/20'
                    : 'cursor-not-allowed opacity-60',
                )}
                onSelect={(e) => {
                  if (account && isAuthorized) onItemClicked('report', e);
                }}
                aria-label={
                  account && isAuthorized
                    ? t('game_menuReport')
                    : `${t('game_menuReport')}: ${t('report_loginRequired')}`
                }
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <ImgReport className="h-6 w-6" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col items-start">
                  <span className="flex items-center">{t('game_menuReport')}</span>
                  {!(account && isAuthorized) && (
                    <span className="mt-0.5 text-xs font-normal text-gray-500">
                      {t('report_loginRequired')}
                    </span>
                  )}
                </span>
              </DropdownMenuItem>

              {/* Leave Game - Always last */}
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="flex w-full flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium text-red-600 transition-colors hover:bg-red-100 focus:bg-red-100 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg"
                  onSelect={() => onItemClicked('leaveGame')}
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <LogoutIcon className="h-6 w-6" />
                  </span>
                  <span className="flex items-center">{t('button_leaveGame')}</span>
                </DropdownMenuItem>
              </>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Invitation Dialog */}
          <InvitationDialog
            isOpen={isInvitationDialogOpen}
            onClose={() => setIsInvitationDialogOpen(false)}
          />
        </>
      </Tooltip.Provider>
    </ClientOnly>
  );
};

export default FloatingMenu;
